import {
  Component,
  OnInit,
  ViewEncapsulation,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarEventTimesChangedEvent,
  CalendarMonthViewDay,
  CalendarView
} from 'angular-calendar';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import { PaymentRoundControllerService } from 'src/app/admin-api/api/paymentRoundController.service';
import { OrphanSchedulerJobResponse } from 'src/app/admin-api/model/orphanSchedulerJobResponse';
import { PaymentRound } from 'src/app/admin-api/model/paymentRound';
import { PaymentRoundRunningStatus } from 'src/app/admin-api/model/paymentRoundRunningStatus';
import { PaymentRoundSummary } from 'src/app/admin-api/model/paymentRoundSummary';
import { PaymentRoundUpsertRequest } from 'src/app/admin-api/model/paymentRoundUpsertRequest';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models/enums/Role';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PaymentRoundReportModalComponent } from './payment-round-report-modal/payment-round-report-modal.component';

@Component({
  selector: 'app-scheduler-jobs',
  templateUrl: './scheduler-jobs.component.html',
  styleUrls: ['./scheduler-jobs.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SchedulerJobsComponent implements OnInit {
  private static readonly EVENT_DURATION_MINUTES: number = 90;
  private static readonly SAFETY_MARGIN_MINUTES: number = 91;
  private static readonly MINIMUM_LEAD_MINUTES: number = 5;
  private static readonly SAFETY_MARGIN_WARNING_LABEL: string =
    'A proxima cobranca pode falhar se a anterior ainda estiver em execucao. Respeite a margem de seguranca de 1 hora e 30 minutos';

  private paymentRoundService = inject(PaymentRoundControllerService);
  private permissionsService = inject(NgxPermissionsService);
  private title = inject(Title);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  permission: WritableSignal<boolean | undefined> = signal(undefined);
  tab: number;
  persistedScheduleTimes: Set<number> = new Set<number>();

  schedulers: PaymentRound[] = [];
  orphanSchedulerJobs: OrphanSchedulerJobResponse[] = [];
  orphanSchedulerCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Round ID')
      .setField('paymentRoundId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Job ID')
      .setField('schedulerJobId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Horario')
      .setField('scheduleTime')
      .setDisplayFunction((item: OrphanSchedulerJobResponse) =>
        this.formatOrphanScheduleTime(item)
      )
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Origem')
      .setField('origin')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Acoes')
      .setType('button')
      .setFilter(false)
      .setSortable(false)
      .build()
  ];
  orphanSchedulerActions: TableActionButton[] = [
    new TableActionButton(
      '',
      (item: OrphanSchedulerJobResponse, $event?: MouseEvent) =>
        this.deleteOrphanJob(item, $event),
      'pi pi-trash',
      () => !!this.permission(),
      '',
      'Remover',
      'bottom',
      true,
      true,
      'danger',
      'small'
    )
  ];

  runningStatus: PaymentRoundRunningStatus | undefined;
  lastRoundSummary: PaymentRoundSummary | undefined;
  now: Date = new Date();

  CalendarView = CalendarView;
  view: CalendarView = CalendarView.Month;
  viewDate: Date = new Date();
  locale = 'pt-BR';
  weekStartsOn: number = 1;
  calendarEvents: CalendarEvent[] = [];
  conflictScheduleTimes: Set<number> = new Set<number>();
  cloneScheduleTooltip: string = '';
  private loadedMonthKeys = new Set<string>();

  get lastRound(): PaymentRound | undefined {
    const roundsWithExecutionDate = this.schedulers.filter(
      (item) => !!item.dateExecuted
    );

    if (!roundsWithExecutionDate.length) {
      return undefined;
    }

    return roundsWithExecutionDate.reduce((latest, current) => {
      const latestDate = new Date(
        latest.dateExecuted as string | Date
      ).getTime();
      const currentDate = new Date(
        current.dateExecuted as string | Date
      ).getTime();

      return currentDate > latestDate ? current : latest;
    });
  }

  get currentRound(): PaymentRound | undefined {
    return this.schedulers.find((item) => this.isTodayJob(item));
  }

  get currentPaymentRoundButtonLabel(): string {
    const totalSubscribers = this.lastRoundSummary?.totalSubscribers;
    const totalProcessed = this.lastRoundSummary?.totalProcessed;

    if (
      totalSubscribers !== undefined &&
      totalProcessed !== undefined &&
      totalSubscribers === totalProcessed
    ) {
      return 'Visualizar última cobrança';
    }

    return 'Visualizar cobrança atual';
  }

  get pendingSubscribersLabel(): string {
    const total = this.currentRound?.subscriberCount ?? 0;
    return `${total.toLocaleString('pt-BR')} assinantes aguardando processamento`;
  }

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.title.setTitle('Cobrancas Agendadas');
      const hasPerm = await this.permissionsService.hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue
      ]);
      this.permission.set(hasPerm);

      this.activatedRoute.queryParams.subscribe((params) => {
        const tabValue = Number(params['tab']);
        this.tab = Number.isNaN(tabValue) ? 0 : tabValue;
      });

      const now = new Date();
      const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' });
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      this.cloneScheduleTooltip = `Copiar agenda de cobranca de ${formatter.format(now)} para ${formatter.format(nextMonth)}`;

      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  navigateTab(tab: number): void {
    this.router.navigate(['/financial/scheduler-jobs'], {
      queryParams: { tab }
    });
  }

  async cloneSchedule(): Promise<void> {
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.paymentRoundService
          .replicateToNextMonth()
          .pipe(map((data) => data.result))
      );
      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
      this.messageService.add({
        severity: 'success',
        summary: 'Agenda clonada',
        detail:
          'A agenda de cobranca foi copiada para o proximo mes com sucesso.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  buildCalendarEvents(): void {
    const scheduleEvents = this.schedulers.map((item) => {
      const scheduleDate = this.getScheduledDate(item);
      const actions: CalendarEventAction[] = [];

      if (this.canRemoveJob(item)) {
        actions.push({
          label: '<i class="pi pi-times"></i>',
          onClick: ({ event, sourceEvent }) =>
            this.cancel(event.meta as PaymentRound, sourceEvent)
        });
      }

      if (this.shouldShowReportAction(item)) {
        actions.push({
          label: '<i class="pi pi-search"></i>',
          onClick: ({ event }) =>
            this.openReportModal(event.meta as PaymentRound)
        });
      }

      if (this.isMissedExecution(item)) {
        actions.push({
          label: '<i class="pi pi-clock"></i>',
          onClick: ({ event, sourceEvent }) =>
            this.confirmRescheduleInFiveMinutes(
              event.meta as PaymentRound,
              sourceEvent
            )
        });
      }

      let color: { primary: string; secondary: string };
      if (this.isPastJob(item)) {
        color = { primary: '#6c757d', secondary: '#e9ecef' };
      } else if (this.isTodayJob(item)) {
        color = { primary: '#0d6efd', secondary: '#cfe2ff' };
      } else if (this.isFutureJob(item) && this.canEditJob(item)) {
        color = { primary: '#198754', secondary: '#d1e7dd' };
      } else if (this.isFutureJob(item)) {
        color = { primary: '#ffc107', secondary: '#fff3cd' };
      } else {
        color = { primary: '#adb5bd', secondary: '#f8f9fa' };
      }

      const cssClasses: string[] = [];
      if (!item.dateExecuted && !this.isPastJob(item)) {
        cssClasses.push('calendar-event-with-margin');
      }
      if (this.isUnsavedSchedule(item)) {
        cssClasses.push('calendar-event-unsaved');
      }
      if (this.conflictScheduleTimes.has(scheduleDate.getTime())) {
        cssClasses.push('calendar-event-conflict-warning');
      }
      if (this.isMissedExecution(item)) {
        cssClasses.push('calendar-event-missed-warning');
      }

      const effectiveDate = (item.dateScheduled ?? item.dateExecuted) as
        | string
        | Date;

      return {
        title: this.isMissedExecution(item)
          ? `Nao executada - ${this.getEventTitle(effectiveDate)}`
          : this.getEventTitle(effectiveDate),
        start: scheduleDate,
        end: this.getScheduleEndDate(effectiveDate),
        meta: item,
        color,
        draggable: this.view !== CalendarView.Month && this.canEditJob(item),
        cssClass: cssClasses.join(' '),
        actions
      } as CalendarEvent;
    });

    this.calendarEvents = scheduleEvents;
  }

  addScheduleFromDate(date: Date): void {
    if (!this.permission()) {
      return;
    }

    const normalizedDate = new Date(date);

    if (
      normalizedDate.getTime() < this.getMinimumAllowedScheduleTime().getTime()
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail:
          'So e permitido adicionar agendamentos com pelo menos 5 minutos de antecedencia.'
      });
      return;
    }

    const conflicts = this.getConflictingSchedules(normalizedDate);
    if (conflicts.length) {
      this.highlightConflicts(conflicts);
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail: SchedulerJobsComponent.SAFETY_MARGIN_WARNING_LABEL
      });
      this.buildCalendarEvents();
      return;
    }

    const alreadyExists = this.schedulers.some(
      (item) =>
        this.getScheduledDate(item).getTime() === normalizedDate.getTime()
    );

    if (alreadyExists) {
      return;
    }

    const newSchedule: PaymentRound = {
      dateScheduled: normalizedDate
    };
    this.schedulers = [...this.schedulers, newSchedule];
    this.clearConflictHighlights();
    this.updateLastRoundFlagByMonth();
    this.buildCalendarEvents();
  }

  handleEventClick({
    event,
    sourceEvent
  }: {
    event: CalendarEvent;
    sourceEvent: MouseEvent | KeyboardEvent;
  }): void {
    const item = event.meta as PaymentRound;

    if (this.isMissedExecution(item)) {
      this.confirmRescheduleInFiveMinutes(item, sourceEvent);
      return;
    }

    if (this.shouldShowReportAction(item)) {
      this.openReportModal(item);
    }
  }

  setView(view: CalendarView): void {
    this.view = view;
    this.buildCalendarEvents();
  }

  async goToPreviousPeriod(): Promise<void> {
    const nextViewDate = new Date(this.viewDate);
    if (this.view === CalendarView.Month) {
      nextViewDate.setMonth(nextViewDate.getMonth() - 1);
    } else if (this.view === CalendarView.Week) {
      nextViewDate.setDate(nextViewDate.getDate() - 7);
    } else {
      nextViewDate.setDate(nextViewDate.getDate() - 1);
    }
    this.viewDate = nextViewDate;
    await this.refreshSchedulersForView();
  }

  async goToNextPeriod(): Promise<void> {
    const nextViewDate = new Date(this.viewDate);
    if (this.view === CalendarView.Month) {
      nextViewDate.setMonth(nextViewDate.getMonth() + 1);
    } else if (this.view === CalendarView.Week) {
      nextViewDate.setDate(nextViewDate.getDate() + 7);
    } else {
      nextViewDate.setDate(nextViewDate.getDate() + 1);
    }
    this.viewDate = nextViewDate;
    await this.refreshSchedulersForView();
  }

  async goToToday(): Promise<void> {
    this.now = new Date();
    this.viewDate = this.now;
    await this.refreshSchedulersForView();
  }

  onMonthDayClicked({ day }: { day: CalendarMonthViewDay }): void {
    if (!day?.date) {
      return;
    }

    this.viewDate = day.date;
    this.setView(CalendarView.Day);
  }

  onHourSegmentClicked({ date }: { date: Date }): void {
    this.addScheduleFromDate(date);
  }

  onEventTimesChanged({
    event,
    newStart
  }: CalendarEventTimesChangedEvent): void {
    const item = event.meta as PaymentRound;
    if (!newStart || !this.canEditJob(item)) {
      return;
    }

    if (newStart.getTime() < this.getMinimumAllowedScheduleTime().getTime()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail:
          'Nao e permitido mover agendamento para uma data/horario com menos de 5 minutos de antecedencia.'
      });
      this.buildCalendarEvents();
      return;
    }

    const conflicts = this.getConflictingSchedules(newStart, item);
    if (conflicts.length) {
      this.highlightConflicts(conflicts);
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail: SchedulerJobsComponent.SAFETY_MARGIN_WARNING_LABEL
      });
      this.buildCalendarEvents();
      return;
    }

    item.dateScheduled = newStart;
    this.schedulers = [...this.schedulers];
    this.clearConflictHighlights();
    this.updateLastRoundFlagByMonth();
    this.buildCalendarEvents();
  }

  removeSchedule(item: PaymentRound): void {
    this.schedulers = this.schedulers.filter((scheduler) => scheduler !== item);
    this.buildCalendarEvents();
  }

  isUnsavedSchedule(item: PaymentRound): boolean {
    const scheduleTimestamp = this.getScheduledDate(item).getTime();
    if (Number.isNaN(scheduleTimestamp)) {
      return false;
    }

    return !this.persistedScheduleTimes.has(scheduleTimestamp);
  }

  isPastJob(item: PaymentRound): boolean {
    if (!item.dateScheduled && !item.dateExecuted) {
      return false;
    }
    const exec = this.getScheduledDate(item);
    return exec < this.startOfToday();
  }

  isTodayJob(item: PaymentRound): boolean {
    if (!item.dateScheduled && !item.dateExecuted) {
      return false;
    }
    const exec = this.getScheduledDate(item);
    return exec >= this.startOfToday() && exec < this.startOfTomorrow();
  }

  isFutureJob(item: PaymentRound): boolean {
    if (!item.dateScheduled && !item.dateExecuted) {
      return false;
    }
    const exec = this.getScheduledDate(item);
    return exec >= this.startOfTomorrow();
  }

  canEditJob(item: PaymentRound): boolean {
    if (!this.permission()) {
      return false;
    }
    const exec = this.getScheduledDate(item);
    if (Number.isNaN(exec.getTime())) {
      return false;
    }
    return exec.getTime() >= this.getMinimumAllowedScheduleTime().getTime();
  }

  canRemoveJob(item: PaymentRound): boolean {
    if (!this.permission()) {
      return false;
    }
    if (item?.lastRound) {
      return false;
    }
    return this.canEditJob(item);
  }

  shouldShowReportAction(item: PaymentRound): boolean {
    return !!item.dateExecuted || this.isPastJob(item) || this.isTodayJob(item);
  }

  isMissedExecution(item: PaymentRound): boolean {
    if (item.dateExecuted) {
      return false;
    }

    const scheduledDate = this.getScheduledDate(item);
    if (Number.isNaN(scheduledDate.getTime())) {
      return false;
    }

    // Considera como nao executada quando o horario agendado ja passou.
    return scheduledDate.getTime() < this.getReferenceNow().getTime();
  }

  startOfToday(): Date {
    const d = this.getReferenceNow();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  startOfTomorrow(): Date {
    const d = this.getReferenceNow();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  }

  async loadSchedulers(): Promise<void> {
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.paymentRoundService
          .getPaymentRoundSummary()
          .pipe(map((data) => data.result))
      );
      this.schedulers = result?.paymentRounds || [];
      this.orphanSchedulerJobs = result?.orphanSchedulerJobs || [];
      await this.loadLastRoundSummary();
      this.clearConflictHighlights();
      this.updateLastRoundFlagByMonth();
      this.persistedScheduleTimes = new Set(
        this.schedulers
          .map((item) => this.getScheduledDate(item).getTime())
          .filter((timestamp) => !Number.isNaN(timestamp))
      );
      // Mark all months already covered by the summary as loaded
      this.loadedMonthKeys.clear();
      this.schedulers.forEach((item) => {
        const d = this.getScheduledDate(item);
        if (!Number.isNaN(d.getTime())) {
          this.loadedMonthKeys.add(this.getMonthKey(d));
        }
      });
      // Always mark the current month as loaded
      this.loadedMonthKeys.add(this.getMonthKey(new Date()));
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async loadRunningStatus(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.runningStatus = await lastValueFrom(
        this.paymentRoundService
          .getRunningStatus()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async saveSchedulers(): Promise<void> {
    LoaderService.showLoader();
    try {
      const payload: PaymentRoundUpsertRequest[] = this.schedulers
        .map((item) => {
          const scheduledDate = this.getScheduledDate(item);
          if (Number.isNaN(scheduledDate.getTime())) {
            return null;
          }

          return {
            dateScheduled: scheduledDate,
            paymentRoundId: item.paymentRoundId,
            subscriberCount: item.subscriberCount
          } as PaymentRoundUpsertRequest;
        })
        .filter((item): item is PaymentRoundUpsertRequest => !!item);

      if (!payload.length) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atencao',
          detail: 'Inclua ao menos um agendamento no calendario.'
        });
        return;
      }

      await lastValueFrom(
        this.paymentRoundService.createOrUpdatePaymentRound(payload)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Salvo',
        detail: 'Cobranca agendada atualizada.'
      });
      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async refreshSchedulersForView(): Promise<void> {
    const monthsToLoad = this.getVisibleMonthDates().filter(
      (d) => !this.loadedMonthKeys.has(this.getMonthKey(d))
    );

    if (!monthsToLoad.length) {
      this.buildCalendarEvents();
      return;
    }

    LoaderService.showLoader();
    try {
      for (const monthDate of monthsToLoad) {
        const key = this.getMonthKey(monthDate);
        const startDate = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth(),
          1,
          0,
          0,
          0,
          0
        );
        const endDate = new Date(
          monthDate.getFullYear(),
          monthDate.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        const rounds = await lastValueFrom(
          this.paymentRoundService
            .getPaymentRoundsByPeriod(startDate, endDate)
            .pipe(map((data) => data.result || []))
        );
        const existingIds = new Set(
          this.schedulers
            .map((s) => s.paymentRoundId)
            .filter((id) => id != null)
        );
        const newRounds = rounds.filter(
          (r) => !existingIds.has(r.paymentRoundId)
        );
        this.schedulers = [...this.schedulers, ...newRounds];
        this.loadedMonthKeys.add(key);
      }
      this.clearConflictHighlights();
      this.updateLastRoundFlagByMonth();
      this.buildCalendarEvents();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  openReportModal(item: PaymentRound): void {
    const effectiveDate = item?.dateScheduled ?? item?.dateExecuted;
    const dateStr = effectiveDate
      ? new Date(effectiveDate as string | Date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      : '';

    this.dialogService.open(PaymentRoundReportModalComponent, {
      data: {
        job: {
          paymentRoundId: item?.paymentRoundId,
          dateScheduled: item?.dateScheduled,
          dateExecuted: item?.dateExecuted
        }
      },
      header: `Relatorio de Cobranca${dateStr ? ' - ' + dateStr : ''}`,
      width: '680px',
      contentStyle: { overflow: 'auto' }
    });
  }

  viewCurrentPaymentRound(): void {
    if (!this.lastRound) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail: 'Nao existe cobranca atual para visualizacao.'
      });
      return;
    }

    this.openReportModal(this.lastRound);
  }

  confirmRescheduleInFiveMinutes(
    item: PaymentRound,
    sourceEvent?: Event
  ): void {
    this.confirmationService.confirm({
      target: sourceEvent?.target as EventTarget,
      message:
        'Esta cobranca agendada nao foi executada. Deseja reprogramar para daqui 5 minutos?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Reprogramar',
      rejectLabel: 'Fechar',
      accept: async () => {
        await this.rescheduleInFiveMinutes(item);
      }
    });
  }

  async rescheduleInFiveMinutes(item: PaymentRound): Promise<void> {
    LoaderService.showLoader();
    try {
      const rescheduledDate = this.getMinimumAllowedScheduleTime();
      const payload: PaymentRoundUpsertRequest[] = this.schedulers
        .map((scheduler) => {
          const scheduleToPersist =
            scheduler.paymentRoundId === item.paymentRoundId
              ? rescheduledDate
              : this.getScheduledDate(scheduler);

          if (Number.isNaN(scheduleToPersist.getTime())) {
            return null;
          }

          return {
            paymentRoundId: scheduler.paymentRoundId,
            subscriberCount: scheduler.subscriberCount,
            dateScheduled: scheduleToPersist
          } as PaymentRoundUpsertRequest;
        })
        .filter((request): request is PaymentRoundUpsertRequest => !!request);

      await lastValueFrom(
        this.paymentRoundService.createOrUpdatePaymentRound(payload)
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Cobranca reprogramada',
        detail: 'A cobranca foi reprogramada para os proximos 5 minutos.'
      });

      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  cancel(item: PaymentRound, sourceEvent?: Event): void {
    if (item?.lastRound) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atencao',
        detail: 'O ultimo PaymentRound nao pode ser removido.'
      });
      return;
    }

    this.confirmationService.confirm({
      target: sourceEvent?.target as EventTarget,
      message: 'Deseja remover este agendamento?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      accept: async () => {
        LoaderService.showLoader();
        try {
          this.removeSchedule(item);
          this.messageService.add({
            severity: 'success',
            summary: 'Removido',
            detail: 'Agendamento removido. Salve para persistir as alteracoes.'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  confirmStart(event?: MouseEvent): void {
    this.confirmationService.confirm({
      target: event?.target,
      message: 'Deseja iniciar a cobranca atual?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Iniciar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        await this.start();
      }
    });
  }

  confirmPause(event?: MouseEvent): void {
    this.confirmationService.confirm({
      target: event?.target,
      message: 'Deseja interromper a cobranca em andamento?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Interromper',
      rejectLabel: 'Cancelar',
      accept: async () => {
        await this.pause();
      }
    });
  }

  async start(): Promise<void> {
    if (!this.currentRound || this.runningStatus?.running) {
      return;
    }

    LoaderService.showLoader();
    try {
      const startResult = await lastValueFrom(
        this.paymentRoundService
          .startPaymentRound()
          .pipe(map((data) => data.result))
      );

      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
      this.messageService.add({
        severity: 'success',
        summary: 'Cobranca iniciada',
        detail: startResult || 'A rodada de cobranca foi iniciada com sucesso.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async pause(): Promise<void> {
    if (!this.runningStatus?.running) {
      return;
    }

    LoaderService.showLoader();
    try {
      const pauseResult = await lastValueFrom(
        this.paymentRoundService
          .pausePaymentRound()
          .pipe(map((data) => data.result))
      );

      await this.loadSchedulers();
      await this.loadRunningStatus();
      this.buildCalendarEvents();
      this.messageService.add({
        severity: 'info',
        summary: 'Agendamento pausado',
        detail:
          pauseResult ||
          'A rodada de cobranca em execucao foi pausada com sucesso.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async deleteOrphanJob(
    item: OrphanSchedulerJobResponse,
    sourceEvent?: Event
  ): Promise<void> {
    this.confirmationService.confirm({
      target: sourceEvent?.target as EventTarget,
      message: 'Deseja remover este job orfao?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      accept: async () => {
        if (!item?.paymentRoundId) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atencao',
            detail: 'Nao foi possivel identificar o Round ID do job orfao.'
          });
          return;
        }

        LoaderService.showLoader();
        try {
          const result = await lastValueFrom(
            this.paymentRoundService
              .deletePaymentRoundSchedule(item.paymentRoundId)
              .pipe(map((data: any) => data.result as string))
          );

          await this.loadSchedulers();
          this.buildCalendarEvents();
          this.messageService.add({
            severity: 'success',
            summary: 'Removido',
            detail: result || 'Job orfao removido com sucesso.'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  private getScheduleTimeLabel(scheduleTime: string | Date): string {
    const scheduleDate = new Date(scheduleTime);
    if (Number.isNaN(scheduleDate.getTime())) {
      return 'Horario invalido';
    }

    return scheduleDate.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private getEventTitle(scheduleTime: string | Date): string {
    const start = this.getScheduleTimeLabel(scheduleTime);
    const end = this.getScheduleTimeLabel(
      this.getScheduleEndDate(scheduleTime)
    );
    return `${start} - ${end} (estimativa)`;
  }

  private getScheduleEndDate(scheduleTime: string | Date): Date {
    const scheduleDate = new Date(scheduleTime);
    scheduleDate.setMinutes(
      scheduleDate.getMinutes() + SchedulerJobsComponent.EVENT_DURATION_MINUTES
    );
    return scheduleDate;
  }

  private getMinimumAllowedScheduleTime(): Date {
    const minimumDate = this.getReferenceNow();
    minimumDate.setMinutes(
      minimumDate.getMinutes() + SchedulerJobsComponent.MINIMUM_LEAD_MINUTES
    );
    return minimumDate;
  }

  private getReferenceNow(): Date {
    this.now = new Date();
    return new Date(this.now);
  }

  private getScheduledDate(item: PaymentRound): Date {
    return new Date((item.dateScheduled ?? item.dateExecuted) as string | Date);
  }

  private async loadLastRoundSummary(): Promise<void> {
    this.lastRoundSummary = undefined;

    if (!this.lastRound?.paymentRoundId) {
      return;
    }

    this.lastRoundSummary = await lastValueFrom(
      this.paymentRoundService
        .getPaymentRoundSummaryById(this.lastRound.paymentRoundId)
        .pipe(map((data) => data.result as PaymentRoundSummary))
    );
  }

  private updateLastRoundFlagByMonth(): void {
    const groups: Record<string, PaymentRound[]> = {};

    for (const item of this.schedulers) {
      const scheduledDate = this.getScheduledDate(item);
      if (Number.isNaN(scheduledDate.getTime())) {
        item.lastRound = false;
        continue;
      }

      const groupKey = `${scheduledDate.getFullYear()}-${scheduledDate.getMonth()}`;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      item.lastRound = false;
    }

    Object.values(groups).forEach((group) => {
      let monthLastRound = group[0];
      group.forEach((item) => {
        const itemDate = this.getScheduledDate(item);
        const lastDate = this.getScheduledDate(monthLastRound);
        if (itemDate.getTime() > lastDate.getTime()) {
          monthLastRound = item;
        }
      });
      monthLastRound.lastRound = true;
    });
  }

  private getMonthKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private getVisibleMonthDates(): Date[] {
    const seen = new Set<string>();
    const result: Date[] = [];
    const add = (d: Date) => {
      const key = this.getMonthKey(d);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(new Date(d));
      }
    };
    add(this.viewDate);
    if (this.view === CalendarView.Week) {
      // A week can span two months — check both edges
      const weekStart = new Date(this.viewDate);
      const dow = weekStart.getDay();
      const diff = (dow - this.weekStartsOn + 7) % 7;
      weekStart.setDate(weekStart.getDate() - diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      add(weekStart);
      add(weekEnd);
    }
    return result;
  }

  private getConflictingSchedules(
    targetStart: Date,
    ignoredItem?: PaymentRound
  ): PaymentRound[] {
    const targetStartDate = new Date(targetStart);
    const marginMs = SchedulerJobsComponent.SAFETY_MARGIN_MINUTES * 60 * 1000;

    return this.schedulers.filter((item) => {
      if (ignoredItem && item === ignoredItem) {
        return false;
      }

      const itemStartDate = this.getScheduledDate(item);
      if (Number.isNaN(itemStartDate.getTime())) {
        return false;
      }

      const diff = Math.abs(
        targetStartDate.getTime() - itemStartDate.getTime()
      );
      return diff < marginMs;
    });
  }

  private highlightConflicts(conflictingSchedules: PaymentRound[]): void {
    this.conflictScheduleTimes = new Set(
      conflictingSchedules
        .map((item) => this.getScheduledDate(item).getTime())
        .filter((timestamp) => !Number.isNaN(timestamp))
    );
  }

  private clearConflictHighlights(): void {
    this.conflictScheduleTimes = new Set<number>();
  }

  private formatOrphanScheduleTime(item: OrphanSchedulerJobResponse): string {
    if (!item?.scheduleTime) {
      return '-';
    }

    return new Date(item.scheduleTime).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
